import React, { PropsWithChildren, useEffect } from 'react';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { AlertGroupName, useAlertState } from '../../hooks/useAlertState/useAlertState';
import { AlertBase, IBaseAlertProps } from '../Alert/AlertBase';
import { AUTO_HIDE_DURATION } from '../ToastMessageManager/ToastMessageManager';
import { GapContainer } from '../../fundamentals/spaces/SpaceContainer';
import { LinkButton } from '../LinkButton/LinkButton';
import { LoadingAnimation } from '../LoadingAnimation/LoadingAnimation';
import styled from 'styled-components';
import { EORA_NUMBERS } from '../../fundamentals/numbers/eoraNumbers';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { MEDIA_QUERY } from '../../fundamentals/breakpoints/breakpoints';

export interface IToastProps extends Omit<IBaseAlertProps, 'groupName' | 'title' | 'light' | 'severityIconOutline' | 'micro'> {}

const ToastContainer = styled(AlertBase)`
  width: ${PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.SIZE_TOAST_WIDTH]};
  ${({ theme }) => theme.mediaQueries[MEDIA_QUERY.MAX][SIZES.SMALL]} {
    width: 100%;
  }
`;

const VerticalContentContainer = styled(GapContainer)``;
const HorizontalContentContainer = styled(GapContainer)`
  justify-content: space-between;
`;

export const Toast = React.forwardRef<HTMLDivElement, PropsWithChildren<IToastProps>>(
  ({ id, onClose, children, isProcessing, processingLabel = 'Processing', onUndo, undoLabel = 'Undo', ...rest }, ref) => {
    const { generateTestId } = useTestId(rest, `Toast-${id}`);
    const { state } = useAlertState(AlertGroupName.Toast);
    const toastState = state.find((item) => item.id === id) ?? { id, children, isProcessing, processingLabel, onUndo, undoLabel, onClose, ...rest };
    let processTimerId: any;

    const handleOnClose = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (processTimerId) {
        clearTimeout(processTimerId);
      }

      if (onClose) {
        onClose(e);
      }
    };

    useEffect(() => {
      if (toastState && isProcessing && !toastState?.isProcessing && onClose) {
        processTimerId = setTimeout(() => onClose(), AUTO_HIDE_DURATION);
      }
      return () => {
        clearTimeout(processTimerId);
      };
    }, [toastState]);

    return (
      <ToastContainer {...generateTestId()} {...{ ...toastState, groupName: AlertGroupName.Toast, ...rest }} ref={ref} onClose={handleOnClose}>
        <HorizontalContentContainer gap={'large'}>
          <VerticalContentContainer column gap={'large'}>
            {toastState.children}
            {toastState.isProcessing && <LoadingAnimation labelPosition='right' variant='white' size='xSmall' label={processingLabel} />}
          </VerticalContentContainer>
          {toastState.onUndo && (
            <LinkButton variant='linkWhite' onClick={toastState.onUndo}>
              {toastState.undoLabel}
            </LinkButton>
          )}
        </HorizontalContentContainer>
      </ToastContainer>
    );
  }
);
